import { ReactElement, useCallback } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { Button, Stack } from '@mui/material'
import { useSnackbar } from '@ui/snackbar'
import { clipboardTextToTableData, tableDataToClipboardText } from 'src/domain/planTables/clipboard'
import { addColumnToTable, createEmptyDataRow } from 'src/domain/planTables/defaults'
import type { TableData } from 'src/domain/planTables/types'
import PlanTableDataGrid from './PlanTableDataGrid'

interface Props {
  table: TableData
  onTableChange: (updater: (table: TableData) => TableData) => void
  readOnly?: boolean
}

/**
 * The editable table grid plus its row/column/clipboard actions, shared by the
 * sidebar form (`PlanTableForm`) and the in-scene edit popup
 * (`PlanTableEditPopup`). Persistence is left to the caller via `onTableChange`
 * (the sidebar persists per change, the popup defers to close).
 */
const PlanTableDataEditor = ({ table, onTableChange, readOnly = false }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()

  const handleAddRow = useCallback(() => {
    onTableChange(currentTable => {
      const columnKeys = currentTable.columns.map(column => column.key)
      const nextIndex = currentTable.rows.length + 1
      return {
        ...currentTable,
        rows: [...currentTable.rows, createEmptyDataRow(columnKeys, nextIndex)],
      }
    })
  }, [onTableChange])

  const handleAddColumn = useCallback(() => {
    onTableChange(currentTable => addColumnToTable(currentTable))
  }, [onTableChange])

  const handleCopyTableData = useCallback(() => {
    navigator.clipboard
      .writeText(tableDataToClipboardText(table))
      .then(() => {
        enqueueSnackbar('Tabellendaten in Zwischenablage kopiert', { variant: 'info' })
      })
      .catch(() => {
        enqueueSnackbar('Tabellendaten konnten nicht kopiert werden', { variant: 'error' })
      })
  }, [enqueueSnackbar, table])

  const handlePasteTableData = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText()
      if (!clipboardText.trim()) {
        enqueueSnackbar('Keine Tabellendaten in der Zwischenablage', { variant: 'warning' })
        return
      }

      const nextTable = clipboardTextToTableData(clipboardText, table.settings)
      onTableChange(() => nextTable)
      enqueueSnackbar('Tabellendaten eingefügt', { variant: 'success' })
    } catch {
      enqueueSnackbar('Tabellendaten konnten nicht eingefügt werden', { variant: 'error' })
    }
  }, [enqueueSnackbar, onTableChange, table.settings])

  return (
    <Stack spacing={1}>
      <PlanTableDataGrid table={table} onTableChange={onTableChange} readOnly={readOnly} />

      {!readOnly && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyTableData}
            data-cy="plan-table-copy-button"
          >
            Kopieren
          </Button>
          <Button
            size="small"
            startIcon={<ContentPasteIcon />}
            onClick={handlePasteTableData}
            data-cy="plan-table-paste-button"
          >
            Einfügen
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            data-cy="plan-table-add-row-button"
          >
            Zeile hinzufügen
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddColumn}
            data-cy="plan-table-add-column-button"
          >
            Spalte hinzufügen
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export default PlanTableDataEditor
